/**
 * Cat vs AI design system primitives — TypeScript port of the GAME_design/atoms.jsx
 * deliverable from Claude Design. Glyphs are static SVG with shared gradient defs;
 * IconTile communicates rarity via frame color + recess spotlight, not via glyph
 * color. Component variants intentionally mirror the design source so future
 * atoms ports stay 1-to-1.
 */
import React, { useEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';

// ─── IconTile: rounded-square recessed tile ───────────────────────────

type IconTileProps = {
  glyph: ReactNode;
  color: string;
  size?: number;
  pulse?: boolean;
  halo?: boolean;
  frame?: boolean;
  radius?: number;
};

export function IconTile({
  glyph,
  color,
  size = 64,
  pulse = false,
  halo = false,
  frame = true,
  radius = 14,
}: IconTileProps) {
  const px = size;
  const inset = frame ? 2 : 0;
  return (
    <div
      className={pulse ? 'tile3d pulse-v2' : 'tile3d'}
      style={{
        width: px,
        height: px,
        borderRadius: radius,
        position: 'relative',
        background: frame
          ? `linear-gradient(180deg,
              color-mix(in srgb, ${color} 90%, white 20%) 0%,
              ${color} 35%,
              color-mix(in srgb, ${color} 55%, #000) 100%)`
          : 'transparent',
        boxShadow: pulse
          ? `0 0 18px ${color}aa, 0 0 36px ${color}55, 0 4px 8px rgba(0,0,0,0.55)`
          : '0 4px 10px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.10)',
        ['--r-glow' as never]: `${color}aa`,
      } as CSSProperties}
    >
      {halo && (
        <div
          style={{
            position: 'absolute',
            inset: -size * 0.2,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color}33 0%, transparent 60%)`,
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset,
          borderRadius: radius - inset,
          overflow: 'hidden',
          background: `radial-gradient(120% 90% at 50% 0%,
            color-mix(in srgb, ${color} 28%, #050a14) 0%,
            color-mix(in srgb, ${color} 12%, #050a14) 35%,
            #03070f 95%)`,
          boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.12),
            inset 0 -3px 6px rgba(0,0,0,0.55),
            inset 0 0 0 1px rgba(0,0,0,0.55)
          `,
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(circle at center, rgba(255,255,255,0.06) 1px, transparent 1.5px)',
            backgroundSize: '6px 6px',
            opacity: 0.5,
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(60% 50% at 50% 55%, ${color}50 0%, transparent 70%)`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.7))',
          }}
        >
          <div
            style={{
              width: '78%',
              height: '78%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {glyph}
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: '50%',
            height: '40%',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '14%',
            left: '16%',
            width: 4,
            height: 4,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.9)',
            boxShadow: '0 0 6px rgba(255,255,255,0.8)',
          }}
        />
      </div>
    </div>
  );
}

// ─── Shared SVG defs (gradients) ───────────────────────────────────────

export function GlyphDefs() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
      <defs>
        <linearGradient id="gW" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#9fb0cf" />
        </linearGradient>
        <linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff4b8" />
          <stop offset="45%" stopColor="#f5c542" />
          <stop offset="100%" stopColor="#7a4f08" />
        </linearGradient>
        <linearGradient id="gGoldDark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f5c542" />
          <stop offset="100%" stopColor="#5a3500" />
        </linearGradient>
        <linearGradient id="gGreen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a8f5b8" />
          <stop offset="100%" stopColor="#1f9c45" />
        </linearGradient>
        <linearGradient id="gCyan" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d2f4ff" />
          <stop offset="100%" stopColor="#1d8fb8" />
        </linearGradient>
        <linearGradient id="gPurple" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e2c7ff" />
          <stop offset="100%" stopColor="#5a2da0" />
        </linearGradient>
        <linearGradient id="gRed" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffc0c0" />
          <stop offset="100%" stopColor="#b32525" />
        </linearGradient>
        <linearGradient id="gOrange" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffd4a0" />
          <stop offset="100%" stopColor="#cc5500" />
        </linearGradient>
        <radialGradient id="rCoin" cx="32%" cy="28%" r="80%">
          <stop offset="0%" stopColor="#fff4b8" />
          <stop offset="45%" stopColor="#f5c542" />
          <stop offset="100%" stopColor="#7a4f08" />
        </radialGradient>
      </defs>
    </svg>
  );
}

// ─── Glyphs ───────────────────────────────────────────────────────────

/** Shared medical cross — used by Med Bay module + hp_up perk (identical SVG). */
const crossGlyph = (
  <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
    <path d="M17 6h10v11h11v10H27v11H17V27H6V17h11Z" fill="url(#gGreen)" stroke="rgba(0,0,0,0.3)" strokeWidth="0.8"/>
    <path d="M17 6h10v5H17Z" fill="rgba(255,255,255,0.35)"/>
  </svg>
);

export const Glyph = {
  syringe: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet">
      <path d="M3 22 L 12 22" stroke="url(#gW)" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 22 L 16 22" stroke="url(#gW)" strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="3" cy="22" r="1.5" fill="url(#gGreen)" />
      <rect x="16" y="15" width="20" height="14" rx="2.5" fill="url(#gW)" stroke="#0d1421" strokeWidth="1" />
      <rect x="17.5" y="16.5" width="13" height="11" rx="1.5" fill="url(#gGreen)" />
      <path d="M19 18 h 2 M 19 21 h 2 M 19 24 h 2" stroke="#0a3a18" strokeWidth="0.8" />
      <path d="M17 16 L 17 27 L 18.5 27 L 18.5 16 Z" fill="rgba(255,255,255,0.4)" />
      <rect x="35" y="11" width="2.5" height="22" rx="1" fill="url(#gW)" />
      <path d="M37.5 22 L 41 22" stroke="url(#gW)" strokeWidth="4" strokeLinecap="round" />
      <rect x="37.5" y="20" width="3" height="1" fill="rgba(255,255,255,0.6)" />
    </svg>
  ),
  lightning: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet">
      <circle cx="22" cy="22" r="16" fill="none" stroke="#f5c542" strokeWidth="1.4" opacity="0.7" />
      <circle cx="22" cy="22" r="13" fill="url(#gGoldDark)" stroke="#5a3500" strokeWidth="0.8" />
      <circle cx="18" cy="18" r="6" fill="rgba(255,255,255,0.35)" />
      <g stroke="#fff8c2" strokeWidth="1.3" strokeLinecap="round">
        <path d="M22 4 v 3 M 40 22 h -3 M 22 40 v -3 M 4 22 h 3" />
        <path d="M34.5 9.5 l -2 2 M 34.5 34.5 l -2 -2 M 9.5 34.5 l 2 -2 M 9.5 9.5 l 2 2" opacity="0.6" />
      </g>
      <path
        d="M25 11 L 15 24 L 21 24 L 19 33 L 29 19 L 23 19 L 26 11 Z"
        fill="#fffae0"
        stroke="#5a3500"
        strokeWidth="0.9"
        strokeLinejoin="round"
      />
      <path d="M25 11 L 17 22 L 20 23 L 21 22 L 24 11 Z" fill="rgba(255,255,255,0.6)" />
    </svg>
  ),
  shield: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet">
      <path
        d="M22 4 L 37 11 L 37 25 C 37 32 30 37 22 40 C 14 37 7 32 7 25 L 7 11 Z"
        fill="url(#gCyan)"
        stroke="#fff"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M22 10 L 32 14 L 32 25 C 32 30 28 33 22 35 C 16 33 12 30 12 25 L 12 14 Z"
        fill="rgba(5,20,40,0.55)"
        stroke="#3dd5ff"
        strokeWidth="0.9"
      />
      <path
        d="M16 23 L 21 28 L 30 18"
        stroke="#fff"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M16 23 L 21 28 L 30 18"
        stroke="#3dd5ff"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M22 4 L 37 11 L 30 13 L 22 7 Z" fill="rgba(255,255,255,0.35)" />
    </svg>
  ),
  ampule: (
    <svg width="100%" height="100%" viewBox="0 0 36 44" fill="none" preserveAspectRatio="xMidYMid meet">
      <rect x="11" y="3" width="14" height="5" rx="1" fill="#c8d0e0" stroke="#000" strokeOpacity="0.4" />
      <rect x="12" y="4" width="5" height="3" fill="rgba(255,255,255,0.7)" />
      <path
        d="M13 8 L 13 13 L 11 15 L 11 35 a 7 7 0 0 0 14 0 L 25 15 L 23 13 L 23 8 Z"
        fill="url(#gPurple)"
        stroke="#fff"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M12 18 L 12 35 a 6 6 0 0 0 12 0 L 24 18 Z" fill="url(#gPurple)" opacity="0.9" />
      <path d="M12 18 L 12 35 a 6 6 0 0 0 12 0 L 24 18 Z" fill="#b76bff" opacity="0.4" />
      <circle cx="16" cy="30" r="1.6" fill="#fff" opacity="0.85" />
      <circle cx="19" cy="34" r="1" fill="#fff" opacity="0.7" />
      <circle cx="14" cy="24" r="0.8" fill="#fff" opacity="0.6" />
      <circle cx="20" cy="26" r="0.6" fill="#fff" opacity="0.5" />
      <path d="M13 17 L 13 34 a 4 4 0 0 0 1 2.5" stroke="rgba(255,255,255,0.55)" strokeWidth="1.4" fill="none" />
    </svg>
  ),
  fish: (
    <svg width="20" height="16" viewBox="0 0 24 20" fill="none" aria-hidden>
      <path
        d="M2 10 C 5 4 11 3 16 5 c 3 1 5 2 5 5 -0 3 -2 4 -5 5 c -5 2 -11 1 -14 -5 z"
        fill="url(#gGold)"
        stroke="#5a3500"
        strokeWidth="0.8"
      />
      <path d="M2 10 L 0 6 L 0 14 Z" fill="#b87f1a" stroke="#5a3500" strokeWidth="0.6" strokeLinejoin="round" />
      <circle cx="17" cy="8" r="1.1" fill="#3a1f00" />
      <circle cx="17.3" cy="7.7" r="0.4" fill="#fff" />
      <path d="M14 7 q -1 3 0 6" stroke="#5a3500" strokeWidth="0.6" fill="none" />
      <path d="M6 6 q 4 -2 8 0" stroke="rgba(255,255,255,0.6)" strokeWidth="1" fill="none" />
    </svg>
  ),
  snowflake: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M7 1v12M1 7h12M2.5 2.5l9 9M11.5 2.5l-9 9" />
    </svg>
  ),

  // ── Module glyphs (44×44 viewBox, used in HideoutSection) ────────────

  /** cryo module */
  moduleSnowflake: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <line x1="22" y1="4" x2="22" y2="40" stroke="url(#gCyan)" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="4" y1="22" x2="40" y2="22" stroke="url(#gCyan)" strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="8" y1="8" x2="36" y2="36" stroke="url(#gCyan)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="36" y1="8" x2="8" y2="36" stroke="url(#gCyan)" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="22" cy="22" r="3.5" fill="url(#gCyan)"/>
      <circle cx="22" cy="8" r="2" fill="url(#gCyan)" opacity="0.7"/>
      <circle cx="22" cy="36" r="2" fill="url(#gCyan)" opacity="0.7"/>
      <circle cx="8" cy="22" r="2" fill="url(#gCyan)" opacity="0.7"/>
      <circle cx="36" cy="22" r="2" fill="url(#gCyan)" opacity="0.7"/>
    </svg>
  ),

  /** med module */
  moduleCross: crossGlyph,

  /** lab module */
  moduleBeaker: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M16 6h12v14l9 17a4 4 0 01-3.5 6h-23A4 4 0 017 37L16 20V6z" fill="url(#gPurple)" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/>
      <line x1="13" y1="28" x2="31" y2="28" stroke="rgba(255,255,255,0.5)" strokeWidth="1.2"/>
      <circle cx="18" cy="35" r="2" fill="rgba(255,255,255,0.7)"/>
      <circle cx="25" cy="32" r="1.5" fill="rgba(255,255,255,0.5)"/>
      <path d="M16 6h12v3H16Z" fill="rgba(255,255,255,0.3)"/>
    </svg>
  ),

  /** gym module */
  moduleDumbbell: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <rect x="5" y="14" width="5" height="16" rx="2" fill="url(#gOrange)"/>
      <rect x="10" y="10" width="4" height="24" rx="2" fill="url(#gOrange)"/>
      <rect x="34" y="14" width="5" height="16" rx="2" fill="url(#gOrange)"/>
      <rect x="30" y="10" width="4" height="24" rx="2" fill="url(#gOrange)"/>
      <rect x="14" y="19" width="16" height="6" rx="3" fill="url(#gOrange)"/>
      <rect x="14" y="19" width="16" height="2.5" rx="1.5" fill="rgba(255,255,255,0.3)"/>
    </svg>
  ),

  /** galley module */
  moduleFish: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M4 22c5-10 15-13 24-10 5 2 10 5 10 10-0 5-5 8-10 10C19 35 9 32 4 22z" fill="url(#gGold)" stroke="#5a3500" strokeWidth="0.8"/>
      <path d="M4 22L0 14v16Z" fill="#b87f1a" stroke="#5a3500" strokeWidth="0.6" strokeLinejoin="round"/>
      <circle cx="31" cy="18" r="2.2" fill="#3a1f00"/>
      <circle cx="31.5" cy="17.5" r="0.8" fill="#fff"/>
      <path d="M25 15q-2 7 0 14" stroke="#5a3500" strokeWidth="0.9" fill="none"/>
      <path d="M12 14q8-4 14 0" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" fill="none"/>
    </svg>
  ),

  /** hangar module */
  moduleRocket: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M22 4c7 8 8 18 0 26-8-8-7-18 0-26z" fill="url(#gRed)" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8"/>
      <path d="M15 24l-6 6 6 2z" fill="url(#gRed)" opacity="0.7"/>
      <path d="M29 24l6 6-6 2z" fill="url(#gRed)" opacity="0.7"/>
      <rect x="19" y="30" width="6" height="10" rx="2" fill="url(#gRed)"/>
      <circle cx="22" cy="18" r="4" fill="rgba(5,8,20,0.8)" stroke="#ff8484" strokeWidth="1"/>
      <circle cx="22" cy="18" r="2" fill="#ff8484" opacity="0.7"/>
      <path d="M22 4c3 5 4 10 3 16" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none"/>
    </svg>
  ),

  /** bridge module */
  moduleCrown: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M6 14l6 16h20l6-16-9 6-7-11-7 11z" fill="url(#gGold)" stroke="#5a3500" strokeWidth="1" strokeLinejoin="round"/>
      <rect x="6" y="30" width="32" height="5" rx="2" fill="url(#gGold)"/>
      <circle cx="6" cy="14" r="2.5" fill="#f5c542"/>
      <circle cx="38" cy="14" r="2.5" fill="#f5c542"/>
      <circle cx="22" cy="8" r="2.5" fill="#fff8c2"/>
      <path d="M6 14l5 4 4-6" stroke="rgba(255,255,255,0.4)" strokeWidth="1" fill="none"/>
    </svg>
  ),

  // ── Perk glyphs (44×44 viewBox, used in LoadoutSection) ─────────────

  /** damage_up */
  perkClaw: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M10 38c2-14 7-24 16-30" stroke="url(#gGreen)" strokeWidth="3" strokeLinecap="round"/>
      <path d="M18 38c1-10 4-18 10-24" stroke="url(#gGreen)" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M26 38c0-6 2-12 6-16" stroke="url(#gGreen)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  /** hp_up — identical to Med Bay module glyph. */
  perkCross: crossGlyph,

  /** speed_up */
  perkBolt: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M27 4L10 26h12l-4 14 18-22H24L27 4z" fill="url(#gGold)" stroke="#5a3500" strokeWidth="0.8" strokeLinejoin="round"/>
      <path d="M27 4L12 24l5 1 4-6L27 4z" fill="rgba(255,255,255,0.45)"/>
    </svg>
  ),

  /** fire_rate_up */
  perkTarget: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <circle cx="22" cy="22" r="17" stroke="url(#gGreen)" strokeWidth="1.8"/>
      <circle cx="22" cy="22" r="10" stroke="url(#gGreen)" strokeWidth="1.5"/>
      <circle cx="22" cy="22" r="3.5" fill="url(#gGreen)"/>
      <line x1="22" y1="4" x2="22" y2="9" stroke="url(#gGreen)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="22" y1="35" x2="22" y2="40" stroke="url(#gGreen)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="4" y1="22" x2="9" y2="22" stroke="url(#gGreen)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="35" y1="22" x2="40" y2="22" stroke="url(#gGreen)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  /** crit_chance */
  perkDrop: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M22 4c9 12 14 20 14 26a14 14 0 01-28 0C8 24 13 16 22 4z" fill="url(#gCyan)"/>
      <ellipse cx="17" cy="24" rx="3" ry="5" fill="rgba(255,255,255,0.35)" transform="rotate(-15 17 24)"/>
    </svg>
  ),

  /** regen */
  perkAura: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <circle cx="22" cy="22" r="5" fill="url(#gGreen)"/>
      <circle cx="22" cy="22" r="12" stroke="url(#gGreen)" strokeWidth="1.5" strokeDasharray="4 4"/>
      <circle cx="22" cy="22" r="18" stroke="url(#gGreen)" strokeWidth="1" opacity="0.5"/>
    </svg>
  ),

  /** piercing */
  perkArrow: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <line x1="4" y1="22" x2="40" y2="22" stroke="url(#gCyan)" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M32 13l10 9-10 9V13z" fill="url(#gCyan)"/>
      <circle cx="10" cy="22" r="2" fill="url(#gCyan)" opacity="0.6"/>
      <circle cx="18" cy="22" r="2" fill="url(#gCyan)" opacity="0.8"/>
    </svg>
  ),

  /** ricochet */
  perkRicochet: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M4 8l16 14-8 14" stroke="url(#gCyan)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M20 22l12-12 8 14" stroke="url(#gCyan)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.7"/>
      <circle cx="20" cy="22" r="3" fill="url(#gCyan)"/>
    </svg>
  ),

  /** vampiric */
  perkFang: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M22 4c10 12 13 20 10 28a10 10 0 01-20 0C9 24 12 16 22 4z" fill="url(#gPurple)"/>
      <path d="M16 30l6 8 6-8" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <ellipse cx="17" cy="20" rx="2.5" ry="4" fill="rgba(255,255,255,0.3)" transform="rotate(-10 17 20)"/>
    </svg>
  ),

  /** explosive_kills */
  perkExplosion: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <circle cx="22" cy="22" r="8" fill="url(#gRed)"/>
      <path d="M22 4v5M22 35v5M4 22h5M35 22h5M9 9l3.5 3.5M31.5 31.5L35 35M35 9l-3.5 3.5M12.5 31.5L9 35" stroke="url(#gRed)" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="22" cy="22" r="4" fill="rgba(255,200,100,0.8)"/>
    </svg>
  ),

  /** magnetic */
  perkMagnet: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M10 8a14 14 0 0124 0v14h-8V10a6 6 0 00-8 0v12h-8V8z" fill="url(#gPurple)"/>
      <rect x="6" y="22" width="8" height="7" rx="2" fill="url(#gRed)"/>
      <rect x="30" y="22" width="8" height="7" rx="2" fill="url(#gCyan)"/>
      <path d="M10 8h4v10H10z" fill="rgba(255,255,255,0.3)"/>
      <path d="M30 8h4v10H30z" fill="rgba(255,255,255,0.3)"/>
    </svg>
  ),

  /** shield_cycle */
  perkShield: (
    <svg width="100%" height="100%" preserveAspectRatio="xMidYMid meet" viewBox="0 0 44 44" fill="none" aria-hidden>
      <path d="M22 4L38 11v14C38 33 30 38 22 41 14 38 6 33 6 25V11Z" fill="url(#gCyan)" stroke="rgba(255,255,255,0.2)" strokeWidth="0.8" strokeLinejoin="round"/>
      <path d="M22 11L33 15v11c0 5-4 8-11 10-7-2-11-5-11-10V15Z" fill="rgba(5,20,40,0.55)" stroke="#3dd5ff" strokeWidth="0.9"/>
      <path d="M16 25l6 6 10-10" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M22 4L38 11l-6 2.5-10-5Z" fill="rgba(255,255,255,0.3)"/>
    </svg>
  ),

  // ── Codex + Settings glyphs ──────────────────────────────────────────

  /** Cat silhouette — Codex КОТЫ tab portrait */
  catPortrait: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M10 20 L8 8 L18 16 Z" fill="url(#gW)" stroke="#0d1421" strokeWidth="0.8" strokeLinejoin="round"/>
      <path d="M34 20 L36 8 L26 16 Z" fill="url(#gW)" stroke="#0d1421" strokeWidth="0.8" strokeLinejoin="round"/>
      <ellipse cx="22" cy="26" rx="14" ry="12" fill="url(#gW)" stroke="#9fb0cf" strokeWidth="1"/>
      <rect x="14" y="23" width="16" height="5" rx="2" fill="#3dd5ff" opacity="0.85"/>
      <rect x="15" y="24" width="4" height="2" rx="1" fill="rgba(255,255,255,0.8)"/>
      <rect x="25" y="24" width="4" height="2" rx="1" fill="rgba(255,255,255,0.8)"/>
      <path d="M20 33 L24 33 L22 35 Z" fill="#9fb0cf"/>
    </svg>
  ),

  /** Enemy robot silhouette — Codex ВРАГИ tab portrait */
  enemySilhouette: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <rect x="13" y="14" width="18" height="16" rx="3" fill="url(#gRed)" stroke="#3a0808" strokeWidth="1"/>
      <circle cx="18" cy="21" r="3" fill="#ff4a4a" stroke="none"/>
      <circle cx="26" cy="21" r="3" fill="#ff4a4a" stroke="none"/>
      <circle cx="18" cy="21" r="1.2" fill="#fff"/>
      <circle cx="26" cy="21" r="1.2" fill="#fff"/>
      <path d="M22 14 L22 8 M19 8 L25 8" stroke="#ff8484" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M16 30 L28 30 M17 30 L17 34 M22 30 L22 34 M27 30 L27 34" stroke="#ff8484" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),

  /** Document scroll silhouette — Codex ЛОР tab portrait */
  loreScroll: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M14 8 L14 36 L30 36 L30 18 L20 8 Z" fill="url(#gCyan)" stroke="#3dd5ff" strokeWidth="0.9"/>
      <path d="M20 8 L20 18 L30 18" fill="none" stroke="#3dd5ff" strokeWidth="0.9"/>
      <path d="M17 23 L27 23 M17 27 L27 27 M17 31 L23 31" stroke="rgba(255,255,255,0.7)" strokeWidth="0.9" strokeLinecap="round"/>
    </svg>
  ),

  /** Speaker — Settings АУДИО tile */
  audioKnob: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M12 17 L12 27 L18 27 L26 34 L26 10 L18 17 Z" fill="url(#gCyan)" stroke="#3dd5ff" strokeWidth="0.9" strokeLinejoin="round"/>
      <path d="M30 16 Q35 22 30 28" stroke="#3dd5ff" strokeWidth="2" strokeLinecap="round" fill="none"/>
      <path d="M33 12 Q40 22 33 32" stroke="#3dd5ff" strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.6"/>
    </svg>
  ),

  /** Bell — Settings УВЕДОМЛЕНИЯ tile */
  bellGlyph: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M14 28 L30 28 L27 21 L27 16 a5 5 0 0 0-10 0 L17 21 Z" fill="url(#gGold)" stroke="#5a3500" strokeWidth="1" strokeLinejoin="round"/>
      <path d="M14 28 L30 28" stroke="#5a3500" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M19.5 30 a2.5 2.5 0 0 0 5 0" stroke="#5a3500" strokeWidth="1.2" fill="none"/>
    </svg>
  ),

  /** Trash/reset arrow — Settings СБРОС tile */
  resetArrow: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M13 17 L31 17 M25 11 L31 17 L25 23" stroke="url(#gRed)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13 17 L13 31 L31 31" stroke="url(#gRed)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),

  /** Sliders — Settings preferences tile */
  sliders: (
    <svg width="100%" height="100%" viewBox="0 0 44 44" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M10 15 L34 15 M10 22 L34 22 M10 29 L34 29" stroke="url(#gW)" strokeWidth="1.6" strokeLinecap="round"/>
      <circle cx="18" cy="15" r="3" fill="url(#gGold)" stroke="#5a3500" strokeWidth="0.8"/>
      <circle cx="26" cy="22" r="3" fill="url(#gCyan)" stroke="#0d3a4a" strokeWidth="0.8"/>
      <circle cx="20" cy="29" r="3" fill="url(#gPurple)" stroke="#2d1060" strokeWidth="0.8"/>
    </svg>
  ),
};

// ─── CoinChip with +/- float-up on value change ───────────────────────

type CoinChipProps = {
  value: number;
  label?: string;
  big?: boolean;
};

type Floater = { id: string; delta: number };

export function CoinChip({ value, label = 'рыб', big = false }: CoinChipProps) {
  const tokenSize = big ? 34 : 28;
  const prev = useRef<number | null>(null);
  const [floaters, setFloaters] = useState<Floater[]>([]);

  useEffect(() => {
    if (prev.current === null) {
      prev.current = value;
      return;
    }
    if (prev.current !== value) {
      const delta = value - prev.current;
      prev.current = value;
      if (delta !== 0) {
        const id = Math.random().toString(36).slice(2);
        setFloaters((f) => [...f, { id, delta }]);
        setTimeout(() => setFloaters((f) => f.filter((x) => x.id !== id)), 2000);
      }
    }
  }, [value]);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: big ? '6px 16px 6px 6px' : '4px 13px 4px 4px',
        borderRadius: 999,
        background: 'linear-gradient(180deg, #2a3756 0%, #0c1428 100%)',
        boxShadow: `
          inset 0 1px 0 rgba(255,255,255,0.14),
          inset 0 -2px 4px rgba(0,0,0,0.5),
          0 4px 12px -4px rgba(0,0,0,0.55),
          0 0 0 1px rgba(245,197,66,0.22)
        `,
        fontFamily: 'var(--f-num)',
        fontSize: big ? 18 : 15,
        color: '#fff',
        position: 'relative',
      }}
    >
      <span
        style={{
          width: tokenSize,
          height: tokenSize,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 30% 25%, #fff8d2, #f5c542 45%, #7a4f08)',
          boxShadow: `
            inset 0 -3px 4px rgba(0,0,0,0.45),
            inset 0 1.5px 1.5px rgba(255,255,255,0.75),
            0 0 0 1.5px #b87f1a,
            0 0 10px rgba(245,197,66,0.45)
          `,
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <svg width={tokenSize * 0.62} height={tokenSize * 0.5} viewBox="0 0 24 18" fill="none" aria-hidden>
          <path
            d="M2 9 C 5 4 11 3 16 5 c 3 1 5 2 5 4 c 0 2 -2 3 -5 4 c -5 2 -11 1 -14 -4 z"
            fill="#5a3500"
          />
          <path d="M2 9 L 0 5 L 0 13 Z" fill="#5a3500" />
          <circle cx="17" cy="7.5" r="0.9" fill="#f5c542" />
        </svg>
      </span>
      <span style={{ letterSpacing: '0.02em', color: '#ffe48a', textShadow: '0 1px 0 rgba(0,0,0,0.5)' }}>
        {value.toLocaleString('ru-RU')}
      </span>
      <span
        style={{
          fontFamily: 'var(--f-body)',
          fontSize: big ? 12 : 11,
          color: 'rgba(255,255,255,0.45)',
          marginLeft: -2,
        }}
      >
        {label}
      </span>
      <span
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          pointerEvents: 'none',
          overflow: 'visible',
        }}
      >
        {floaters.map((f) => (
          <span
            key={f.id}
            className="coin-floater"
            style={{
              position: 'absolute',
              left: '50%',
              top: -2,
              transform: 'translateX(-50%)',
              fontFamily: 'var(--f-display)',
              fontSize: 15,
              color: f.delta > 0 ? '#6cf591' : '#ff8484',
              textShadow:
                f.delta > 0
                  ? '0 0 10px rgba(108,245,145,0.8), 0 1px 2px rgba(0,0,0,0.7)'
                  : '0 0 10px rgba(255,132,132,0.8), 0 1px 2px rgba(0,0,0,0.7)',
              whiteSpace: 'nowrap',
              letterSpacing: '0.04em',
            }}
          >
            {f.delta > 0 ? '+' : ''}
            {f.delta}
          </span>
        ))}
      </span>
    </span>
  );
}

// ─── Section header ───────────────────────────────────────────────────

type SectionHeadProps = {
  title: string;
  right?: ReactNode;
  accent?: string;
};

export function SectionHead({ title, right, accent = '#f5c542' }: SectionHeadProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        gap: 12,
        marginTop: 16,
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
        <span
          style={{
            width: 7,
            height: 7,
            transform: 'rotate(45deg)',
            background: accent,
            boxShadow: `0 0 10px ${accent}`,
          }}
        />
        <span
          style={{
            fontFamily: 'var(--f-display)',
            fontSize: 14,
            letterSpacing: '0.20em',
            color: '#fff',
            textShadow: `0 0 14px ${accent}55`,
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        <span
          style={{
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, ${accent}66 0%, transparent 100%)`,
            minWidth: 12,
          }}
        />
      </div>
      {right && <div style={{ flexShrink: 0 }}>{right}</div>}
    </div>
  );
}

// ─── Stock badge (corner pill on item icon) ───────────────────────────

export function StockBadge({ count }: { count: number }) {
  return (
    <span
      className="stock-badge"
      style={{
        zIndex: 2,
      }}
    >
      <span className="x">×</span>
      {count}
    </span>
  );
}
